import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Plus, Mail, Users as UsersIcon, BarChart3, Trash2, Power, RefreshCw, Upload, Link as LinkIcon, Share2, Check, X, Download, ShieldCheck, Clock, Filter } from "lucide-react";
import { toast } from "sonner";

function initialsOf(s) {
  return (s || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
}

/** A user counts as "online" if they hit any authenticated route in the last
 * ~5 minutes — long enough to cover idle pauses, short enough to be useful. */
const ONLINE_WINDOW_MS = 5 * 60 * 1000;
function isOnline(u) {
  const ts = u?.last_seen_at;
  if (!ts) return false;
  const last = new Date(ts).getTime();
  if (Number.isNaN(last)) return false;
  return (Date.now() - last) < ONLINE_WINDOW_MS;
}
function lastSeenLabel(u) {
  const ts = u?.last_seen_at;
  if (!ts) return "Never signed in";
  const ms = Date.now() - new Date(ts).getTime();
  if (Number.isNaN(ms)) return "—";
  if (ms < ONLINE_WINDOW_MS) return "Online now";
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `Last seen ${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `Last seen ${hours}h ago`;
  const days = Math.round(hours / 24);
  return `Last seen ${days}d ago`;
}

function OnlineDot({ user }) {
  const online = isOnline(user);
  return (
    <span
      title={lastSeenLabel(user)}
      aria-label={online ? "Online now" : lastSeenLabel(user)}
      data-testid={online ? `online-dot-${user.id}` : `offline-dot-${user.id}`}
      className={
        "inline-block w-2 h-2 rounded-full shrink-0 " +
        (online
          ? "bg-emerald-500 ring-2 ring-emerald-500/20 animate-pulse"
          : "bg-zinc-300")
      }
    />
  );
}

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("users");

  // If we arrived from a Gmail OAuth redirect, jump to the Inboxes tab so the user sees the result.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("gmail")) setTab("inboxes");
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFB]" data-testid="admin-page">
      <div className="h-14 border-b border-zinc-200 bg-white px-5 flex items-center gap-3">
        <button
          onClick={() => navigate("/inbox")}
          className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-md transition-colors"
          data-testid="back-to-inbox"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <h1 className="font-heading text-lg font-medium tracking-tight">Admin Panel</h1>
        <span className="ml-auto text-xs text-zinc-500 font-mono">{user?.email}</span>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-transparent border border-zinc-200 rounded-md p-0.5">
            <TabsTrigger value="users" className="data-[state=active]:bg-[#0033CC] data-[state=active]:text-white rounded-sm text-xs px-3" data-testid="admin-tab-users">
              <UsersIcon className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Team
            </TabsTrigger>
            <TabsTrigger value="inboxes" className="data-[state=active]:bg-[#0033CC] data-[state=active]:text-white rounded-sm text-xs px-3" data-testid="admin-tab-inboxes">
              <Mail className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Inboxes
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-[#0033CC] data-[state=active]:text-white rounded-sm text-xs px-3" data-testid="admin-tab-analytics">
              <BarChart3 className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Analytics
            </TabsTrigger>
            {user?.role === "owner" && (
              <TabsTrigger value="audit" className="data-[state=active]:bg-[#0033CC] data-[state=active]:text-white rounded-sm text-xs px-3" data-testid="admin-tab-audit">
                <ShieldCheck className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Audit log
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
          <TabsContent value="inboxes" className="mt-6"><InboxesTab /></TabsContent>
          <TabsContent value="analytics" className="mt-6"><AnalyticsTab /></TabsContent>
          {user?.role === "owner" && (
            <TabsContent value="audit" className="mt-6"><AuditLogTab /></TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const [invitePhone, setInvitePhone] = useState("");
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkRole, setBulkRole] = useState("admin");
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "admin", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const [u, p, link] = await Promise.all([
      api.get("/users"),
      api.get("/users/pending"),
      api.get("/invites/signup-link"),
    ]);
    setUsers(u.data);
    setPending(p.data || []);
    setInviteLink(link.data?.url || "");
  };
  useEffect(() => {
    load();
    // Keep online dots fresh — refetch /users every 30s so we pick up
    // changes to last_seen_at without manual refresh.
    const t = setInterval(() => {
      api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, []);

  const approveUser = async (u) => {
    try {
      await api.post(`/users/${u.id}/approve`);
      toast.success(`${u.name} approved — welcome email sent`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't approve");
    }
  };

  const declineUser = async (u) => {
    if (!confirm(`Decline ${u.name} (${u.email})?`)) return;
    try {
      await api.post(`/users/${u.id}/decline`);
      toast.success("Request declined");
      load();
    } catch (e) {
      toast.error("Couldn't decline");
    }
  };

  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Invite link copied");
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };

  const shareInviteLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Internal Inbox",
          text: "You've been invited to join Internal Inbox. Sign up here:",
          url: inviteLink,
        });
      } catch (e) { void e; }
    } else {
      copyInviteLink();
    }
  };

  const sendInviteSms = async () => {
    if (!invitePhone.trim()) { toast.error("Enter a phone number"); return; }
    try {
      await api.post("/invites/send-sms", { phone: invitePhone });
      toast.success(`Invite SMS sent to ${invitePhone}`);
      setInvitePhone("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send SMS");
    }
  };

  const create = async () => {
    setSubmitting(true);
    try {
      await api.post("/users", form);
      toast.success("User added");
      setOpen(false);
      setForm({ email: "", password: "", name: "", role: "admin", phone: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  const submitBulk = async () => {
    // Smart CSV parser — tolerates whatever column layout the user pastes.
    // Detects a header row (any line that contains no '@'), maps headers to
    // column indexes (name | email | role | password | status), then pulls
    // the corresponding field from each data row. Falls back to positional
    // parsing if no header is detected.
    const lines = bulkText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) {
      toast.error("Paste at least one row.");
      return;
    }

    // Detect header row: first line WITHOUT an @ is treated as the header.
    let headerIdx = null;
    if (!lines[0].includes("@")) headerIdx = 0;

    const colMap = { name: -1, email: -1, role: -1, password: -1, status: -1 };
    if (headerIdx !== null) {
      const cols = lines[headerIdx].split(",").map((c) => c.trim().toLowerCase());
      cols.forEach((c, i) => {
        if (c === "name") colMap.name = i;
        else if (c === "email") colMap.email = i;
        else if (c === "role") colMap.role = i;
        else if (c === "password" || c === "pw") colMap.password = i;
        else if (c === "status" || c === "active") colMap.status = i;
      });
    }

    // Maps free-form role values to our role model.
    const roleOf = (raw) => {
      const r = (raw || "").toLowerCase().trim();
      if (r === "owner") return "owner";
      // "Employee", "Agent", "Member", "Manager", blank → admin (email-only)
      return "admin";
    };

    const rows = [];
    const seenEmails = new Set();
    const dupesInCsv = [];

    const startIdx = headerIdx === null ? 0 : 1;
    for (let i = startIdx; i < lines.length; i++) {
      const parts = lines[i].split(",").map((p) => p.trim());
      let name, email, role, password;

      if (headerIdx !== null && colMap.email >= 0) {
        email = parts[colMap.email] || "";
        name = colMap.name >= 0 ? parts[colMap.name] : "";
        role = roleOf(colMap.role >= 0 ? parts[colMap.role] : bulkRole);
        password = colMap.password >= 0 ? parts[colMap.password] : "";
        // Status column — skip inactive rows.
        if (colMap.status >= 0) {
          const s = (parts[colMap.status] || "").toLowerCase().trim();
          if (s && s !== "active" && s !== "true" && s !== "1") continue;
        }
      } else {
        // Fallback: "Name, email" or "email" per line
        if (parts.length >= 2 && parts[1].includes("@")) {
          name = parts[0];
          email = parts[1];
        } else if (parts[0]?.includes("@")) {
          email = parts[0];
          name = parts[0].split("@")[0].replace(/[._-]/g, " ");
        } else {
          continue;
        }
        role = bulkRole;
        password = "";
      }

      if (!email || !email.includes("@")) continue;
      const emailKey = email.toLowerCase();
      if (seenEmails.has(emailKey)) {
        // Two rows in the CSV share the same email — keep the first, log the rest.
        dupesInCsv.push({ email: emailKey, name });
        continue;
      }
      seenEmails.add(emailKey);

      const row = { name: name || email.split("@")[0], email, role };
      if (password) row.password = password;
      rows.push(row);
    }

    if (rows.length === 0) {
      toast.error("No valid rows found. Need a column with an '@' email address.");
      return;
    }

    setBulkSubmitting(true);
    setBulkResults(null);
    try {
      const { data } = await api.post("/users/bulk", { users: rows });
      // Merge in-CSV duplicate warnings into the result so the admin sees them.
      if (dupesInCsv.length > 0) {
        data.results = data.results || [];
        for (const d of dupesInCsv) {
          data.results.push({ email: d.email, ok: false, reason: "Duplicate email in CSV (kept the first row)" });
        }
        data.skipped = (data.skipped || 0) + dupesInCsv.length;
      }
      setBulkResults(data);
      toast.success(`${data.created} users created · ${data.skipped} skipped`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkSubmitting(false);
    }
  };

  const onCsvFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setBulkText(text);
      toast.success(`Loaded ${file.name}`);
    } catch (err) {
      toast.error("Couldn't read file");
    }
    // Reset the input so picking the same file again re-fires
    e.target.value = "";
  };

  const downloadCredentials = () => {
    if (!bulkResults) return;
    const ok = bulkResults.results.filter((r) => r.ok);
    const csv = "name,email,password,role\n" +
      ok.map((r) => `"${(r.name || "").replace(/"/g, '""')}",${r.email},${r.password},${r.role}`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `internal-inbox-credentials-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleActive = async (u) => {
    await api.put(`/users/${u.id}`, { active: !u.active });
    toast.success(u.active ? "User disabled" : "User enabled");
    load();
  };

  const changeRole = async (u, role) => {
    await api.put(`/users/${u.id}`, { role });
    toast.success("Role updated");
    load();
  };

  const remove = async (u) => {
    if (!confirm(`Remove ${u.name}?`)) return;
    await api.delete(`/users/${u.id}`);
    toast.success("User removed");
    load();
  };

  return (
    <div className="space-y-4">
      {/* Pending approvals */}
      {pending.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded-md" data-testid="pending-approvals">
          <div className="px-4 py-3 border-b border-amber-200">
            <h2 className="font-heading text-base font-medium tracking-tight text-amber-900">
              Pending approval ({pending.length})
            </h2>
            <p className="text-xs text-amber-800 mt-0.5">New signups waiting on your call.</p>
          </div>
          <div className="divide-y divide-amber-200">
            {pending.map((u) => (
              <div key={u.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`pending-row-${u.id}`}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Avatar className="w-9 h-9"><AvatarFallback className="text-xs bg-amber-200 text-amber-900">{initialsOf(u.name)}</AvatarFallback></Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{u.name}</p>
                    <p className="font-mono text-[11px] text-zinc-600 truncate">{u.email}</p>
                    {u.phone && <p className="font-mono text-[11px] text-zinc-500">{u.phone}</p>}
                    {u.signup_reason && <p className="text-xs text-zinc-700 mt-1 italic">"{u.signup_reason}"</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => approveUser(u)}
                    data-testid={`approve-${u.id}`}
                  >
                    <Check className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={() => declineUser(u)}
                    data-testid={`decline-${u.id}`}
                  >
                    <X className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    <div className="border border-zinc-200 bg-white rounded-md">
      <div className="px-4 py-3 border-b border-zinc-200 flex items-start sm:items-center justify-between gap-2 flex-col sm:flex-row">
        <div>
          <h2 className="font-heading text-base font-medium tracking-tight">Team members</h2>
          <p className="text-xs text-zinc-500 mt-0.5">{users.length} member{users.length === 1 ? "" : "s"} · disable instantly to revoke access</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="h-8 bg-[#0033CC] hover:bg-[#002299] text-white" data-testid="invite-button">
                <LinkIcon className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} /> Generate signup link
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] max-w-md max-h-[95vh] overflow-y-auto" data-testid="invite-dialog">
              <DialogHeader>
                <DialogTitle>Invite new users</DialogTitle>
                <DialogDescription>
                  Share this link with anyone you want to onboard. They'll fill out a form and you'll see them in <span className="font-medium text-amber-700">Pending approval</span> below.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input value={inviteLink} readOnly className="font-mono text-xs flex-1" data-testid="invite-link-input" />
                  <Button onClick={copyInviteLink} variant="outline" size="sm" className="h-9 shrink-0" data-testid="copy-invite-link">
                    Copy
                  </Button>
                </div>
                <Button onClick={shareInviteLink} variant="outline" className="w-full h-10" data-testid="share-invite-link">
                  <Share2 className="w-4 h-4 mr-2" strokeWidth={1.75} /> Share via…
                </Button>
                <div className="border-t border-zinc-200 pt-3">
                  <p className="text-xs font-semibold tracking-[0.05em] uppercase text-zinc-500 mb-2">Or text the link</p>
                  <div className="flex gap-2">
                    <Input
                      type="tel"
                      value={invitePhone}
                      onChange={(e) => setInvitePhone(e.target.value)}
                      placeholder="+15165551234"
                      className="flex-1 text-sm font-mono"
                      data-testid="invite-phone-input"
                    />
                    <Button onClick={sendInviteSms} className="h-9 bg-[#0033CC] hover:bg-[#002299]" data-testid="send-invite-sms">
                      Text it
                    </Button>
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-1.5">
                    Requires a Twilio sender phone number (TWILIO_FROM_NUMBER env var).
                    If not set, just copy the link and share manually.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteOpen(false)}>Done</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) { setBulkText(""); setBulkResults(null); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="h-8" data-testid="bulk-import-button">
                <Plus className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} /> Bulk import
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl w-[95vw] max-h-[95vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Bulk add team members</DialogTitle>
                <DialogDescription>
                  Paste one user per line. Format: <code className="px-1 bg-zinc-100 rounded text-xs">Name, email</code> — or just the email.
                  Domain must be allowed (e.g., <span className="font-mono">@choicecapitalsolutions.com</span>). Passwords are auto-generated.
                </DialogDescription>
              </DialogHeader>
              {!bulkResults ? (
                <>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <label className="inline-flex items-center gap-1.5 px-3 h-8 border border-zinc-200 rounded-md text-xs cursor-pointer hover:bg-zinc-50 transition-colors" data-testid="bulk-csv-label">
                        <Upload className="w-3.5 h-3.5" strokeWidth={1.75} />
                        Upload CSV file
                        <input type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={onCsvFile} data-testid="bulk-csv-file" />
                      </label>
                      <span className="text-[10px] text-zinc-500">or paste rows below</span>
                    </div>
                    <textarea
                      value={bulkText}
                      onChange={(e) => setBulkText(e.target.value)}
                      rows={10}
                      placeholder={"John Smith, john@choicecapitalsolutions.com\nJane Doe, jane@choicecapitalsolutions.com\nmike@choicecapitalsolutions.com"}
                      className="w-full text-sm font-mono border border-zinc-200 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-[#0033CC]"
                      data-testid="bulk-textarea"
                    />
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs">Role for all</Label>
                        <Select value={bulkRole} onValueChange={setBulkRole}>
                          <SelectTrigger className="h-8 w-32 text-xs" data-testid="bulk-role"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin (email only)</SelectItem>
                            <SelectItem value="owner">Owner (full access)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <span className="text-[10px] text-zinc-500" data-testid="bulk-row-count">
                        {bulkText.split(/\r?\n/).filter((l) => l.includes("@")).length} valid row(s)
                      </span>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
                    <Button onClick={submitBulk} disabled={bulkSubmitting || !bulkText.trim()} className="bg-[#0033CC] hover:bg-[#002299]" data-testid="bulk-submit">
                      {bulkSubmitting ? "Importing…" : "Import"}
                    </Button>
                  </DialogFooter>
                </>
              ) : (
                <>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{bulkResults.created} created</Badge>
                      <Badge variant="secondary">{bulkResults.skipped} skipped</Badge>
                      {bulkResults.created > 0 && (
                        <Button size="sm" variant="outline" className="ml-auto h-8" onClick={downloadCredentials} data-testid="bulk-download-csv">
                          Download credentials CSV
                        </Button>
                      )}
                    </div>
                    <div className="max-h-72 overflow-y-auto border border-zinc-200 rounded-md">
                      <table className="w-full text-xs">
                        <thead className="bg-zinc-50">
                          <tr><th className="text-left p-2">Email</th><th className="text-left p-2">Result</th><th className="text-left p-2">Password</th></tr>
                        </thead>
                        <tbody>
                          {bulkResults.results.map((r, i) => (
                            <tr key={i} className="border-t border-zinc-100">
                              <td className="p-2 font-mono">{r.email}</td>
                              <td className="p-2">
                                {r.ok
                                  ? <span className="text-emerald-700">✓ created</span>
                                  : <span className="text-amber-700">✗ {r.reason}</span>}
                              </td>
                              <td className="p-2 font-mono">{r.password || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-zinc-500">
                      Download the CSV and share each row with the corresponding employee. Passwords are shown once.
                    </p>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setBulkOpen(false)}>Close</Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-[#0033CC] hover:bg-[#002299] text-white h-8" data-testid="add-user-button">
              <Plus className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} /> Add member
            </Button>
          </DialogTrigger>
          <DialogContent className="w-[95vw] max-h-[95vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add team member</DialogTitle>
              <DialogDescription>They'll be able to sign in immediately with these credentials.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
              <div><Label className="text-xs">Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" /></div>
              <div>
                <Label className="text-xs">Phone (for SMS login)</Label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+15165551234"
                  data-testid="new-user-phone"
                />
                <p className="text-[10px] text-zinc-500 mt-1">Optional. E.164 format (e.g. +15165551234). 10-digit US numbers OK too.</p>
              </div>
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin (email only)</SelectItem>
                    <SelectItem value="owner">Owner (full access)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={create} disabled={submitting || !form.email || !form.password || !form.name} className="bg-[#0033CC] hover:bg-[#002299]" data-testid="submit-new-user">
                {submitting ? "Adding…" : "Add member"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Avatar className="w-7 h-7"><AvatarFallback className="text-xs bg-zinc-200">{initialsOf(u.name)}</AvatarFallback></Avatar>
                  <OnlineDot user={u} />
                  <span className="font-medium text-sm">{u.name}</span>
                </div>
              </TableCell>
              <TableCell className="font-mono text-xs">{u.email}</TableCell>
              <TableCell>
                <PhoneCell user={u} onSaved={load} />
              </TableCell>
              <TableCell>
                <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                  <SelectTrigger className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="owner">Owner</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                <Badge variant={u.active ? "default" : "secondary"} className={u.active ? "bg-green-100 text-green-700 hover:bg-green-100" : "bg-red-100 text-red-700 hover:bg-red-100"}>
                  {u.active ? "Active" : "Disabled"}
                </Badge>
              </TableCell>
              <TableCell className="text-right space-x-1">
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`}>
                  <Power className="w-3 h-3 mr-1" strokeWidth={1.5} /> {u.active ? "Disable" : "Enable"}
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs text-red-600 hover:text-red-700" onClick={() => remove(u)} data-testid={`remove-user-${u.id}`}>
                  <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {users.map((u) => (
          <div key={u.id} className="border border-zinc-200 rounded-lg p-3 bg-white" data-testid={`user-card-${u.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <Avatar className="w-9 h-9"><AvatarFallback className="text-xs bg-zinc-200">{initialsOf(u.name)}</AvatarFallback></Avatar>
              <OnlineDot user={u} />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{u.name}</p>
                <p className="font-mono text-[11px] text-zinc-600 truncate">{u.email}</p>
              </div>
              <Badge variant="default" className={u.active ? "bg-green-100 text-green-700 hover:bg-green-100" : "bg-red-100 text-red-700 hover:bg-red-100"}>
                {u.active ? "Active" : "Disabled"}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs mb-2">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-zinc-500 mb-0.5">Phone</p>
                <PhoneCell user={u} onSaved={load} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-zinc-500 mb-0.5">Role</p>
                <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="owner">Owner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 pt-2 border-t border-zinc-100">
              <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={() => toggleActive(u)}>
                <Power className="w-3 h-3 mr-1" strokeWidth={1.5} /> {u.active ? "Disable" : "Enable"}
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs text-red-600 hover:text-red-700" onClick={() => remove(u)}>
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
    </div>
  );
}

function InboxesTab() {
  const [inboxes, setInboxes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email_address: "", display_name: "" });
  const [connecting, setConnecting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/inboxes");
    setInboxes(data);
  };
  useEffect(() => {
    load();
    // Handle return from Google OAuth
    const params = new URLSearchParams(window.location.search);
    const gmail = params.get("gmail");
    if (gmail === "connected") {
      toast.success(`Gmail connected: ${params.get("email") || ""}`);
      window.history.replaceState({}, "", "/admin");
    } else if (gmail === "error") {
      const reason = params.get("reason") || "unknown";
      const detail = params.get("detail");
      toast.error(`Gmail connection failed: ${reason}${detail ? ` — ${detail}` : ""}`, { duration: 15000 });
      window.history.replaceState({}, "", "/admin");
    }
  }, []);

  const connectGmail = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/gmail/oauth/start");
      window.location.href = data.url;
    } catch (e) {
      toast.error("Failed to start Gmail OAuth");
      setConnecting(false);
    }
  };

  const connect = async () => {
    try {
      await api.post("/inboxes", form);
      toast.success("Mock inbox added");
      setOpen(false);
      setForm({ email_address: "", display_name: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const disconnect = async (i) => {
    if (!confirm(`Disconnect ${i.email_address}?`)) return;
    await api.delete(`/inboxes/${i.id}`);
    toast.success("Inbox disconnected");
    load();
  };

  const sync = async (i) => {
    const { data } = await api.post(`/inboxes/${i.id}/sync`);
    toast.success(`Sync queued · ${data.mode} mode`);
  };

  // ---- Historic backfill ----
  const [backfillRows, setBackfillRows] = useState({}); // inbox_id -> status payload

  const fetchBackfillStatus = async (id) => {
    try {
      const { data } = await api.get(`/inboxes/${id}/backfill/status`);
      setBackfillRows((prev) => ({ ...prev, [id]: data }));
    } catch (e) { void e; }
  };

  useEffect(() => {
    // Poll backfill status every 5s while ANY inbox is running a backfill.
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const live = inboxes.filter((i) => !i.mock_mode);
      await Promise.all(live.map((i) => fetchBackfillStatus(i.id)));
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [inboxes]);

  const syncEverything = async (i) => {
    if (!window.confirm(
      `Pull every email in ${i.email_address} and bring the mirror up to right now? This runs in the background — you can keep using the app.`
    )) return;
    try {
      // 1) Kick off a full historic backfill (force=true ignores any stuck
      //    "running" status so the user always gets fresh progress). The
      //    server skips messages we've already mirrored, so this is cheap
      //    after the first complete pass.
      await api.post(`/inboxes/${i.id}/backfill?force=true`);
      // 2) Also fire a normal sync so anything from the last few minutes
      //    that hasn't propagated to history queries yet still lands.
      try { await api.post(`/inboxes/${i.id}/sync`); } catch (_) { /* non-fatal */ }
      toast.success("Pulling all emails — progress shows below.");
      fetchBackfillStatus(i.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start sync");
    }
  };

  return (
    <div className="border border-zinc-200 bg-white rounded-md">
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between gap-2">
        <div>
          <h2 className="font-heading text-base font-medium tracking-tight">Connected inboxes</h2>
          <p className="text-xs text-zinc-500 mt-0.5">One OAuth connection per inbox — all agents read from the mirror.</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="add-inbox-button">
                <Plus className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} /> Add mock
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] max-h-[95vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add a mock inbox</DialogTitle>
                <DialogDescription>For testing only — won't pull real Gmail. Use "Connect Gmail" for real inboxes.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Display name</Label><Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Support team" data-testid="new-inbox-name" /></div>
                <div><Label className="text-xs">Gmail address</Label><Input type="email" value={form.email_address} onChange={(e) => setForm({ ...form, email_address: e.target.value })} placeholder="support@yourcompany.com" data-testid="new-inbox-email" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={connect} disabled={!form.email_address || !form.display_name} className="bg-[#0033CC] hover:bg-[#002299]" data-testid="submit-new-inbox">Add</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Button onClick={connectGmail} disabled={connecting} size="sm" className="bg-[#0033CC] hover:bg-[#002299] text-white h-8" data-testid="connect-gmail-button">
            <Mail className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
            {connecting ? "Opening Google…" : "Connect Gmail"}
          </Button>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Inbox</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Mode</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {inboxes.map((i) => (
            <React.Fragment key={i.id}>
            <TableRow data-testid={`inbox-row-${i.id}`}>
              <TableCell><span className="font-medium text-sm">{i.display_name}</span></TableCell>
              <TableCell className="font-mono text-xs">{i.email_address}</TableCell>
              <TableCell><Badge variant="secondary" className="text-[10px] uppercase">{i.mock_mode ? "Mock" : "Live"}</Badge></TableCell>
              <TableCell><Badge className="bg-green-100 text-green-700 hover:bg-green-100">{i.connected ? "Connected" : "Disconnected"}</Badge></TableCell>
              <TableCell className="text-right space-x-1">
                {!i.mock_mode && (
                  <Button
                    size="sm"
                    className="h-7 text-xs bg-[#0033CC] hover:bg-[#002299] text-white"
                    onClick={() => syncEverything(i)}
                    data-testid={`sync-everything-${i.id}`}
                    title="Pull every email + bring the mirror up to right now"
                  >
                    <Download className="w-3 h-3 mr-1" strokeWidth={2} />
                    {backfillRows[i.id]?.status === "running" ? "Syncing…" : "Sync everything"}
                  </Button>
                )}
                <Button variant="outline" size="sm" className="h-7 text-xs text-red-600" onClick={() => disconnect(i)} data-testid={`disconnect-inbox-${i.id}`}>
                  <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                </Button>
              </TableCell>
            </TableRow>
            {backfillRows[i.id] && backfillRows[i.id].status && backfillRows[i.id].status !== "idle" && (
              <TableRow className="bg-zinc-50" data-testid={`backfill-status-row-${i.id}`}>
                <TableCell colSpan={5} className="py-2">
                  <BackfillProgress status={backfillRows[i.id]} />
                </TableCell>
              </TableRow>
            )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function AnalyticsTab() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/analytics/overview");
        setStats(data);
      } catch (e) {
        void e;
      }
    })();
  }, []);

  if (!stats) return <div className="text-sm text-zinc-500">Loading…</div>;

  const cards = [
    { label: "Total threads", value: stats.total_threads, color: "#0033CC" },
    { label: "Open", value: stats.open, color: "#2563EB" },
    { label: "Pending", value: stats.pending, color: "#F59E0B" },
    { label: "Closed", value: stats.closed, color: "#10B981" },
    { label: "Unassigned", value: stats.unassigned, color: "#71717A" },
    { label: "Unread", value: stats.unread, color: "#EF4444" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-zinc-200 border border-zinc-200 rounded-md overflow-hidden">
        {cards.map((c) => (
          <div key={c.label} className="bg-white p-4">
            <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-1">{c.label}</p>
            <p className="font-heading text-2xl font-medium tracking-tight" style={{ color: c.color }}>{c.value}</p>
          </div>
        ))}
      </div>

      <div className="border border-zinc-200 bg-white rounded-md">
        <div className="px-4 py-3 border-b border-zinc-200">
          <h2 className="font-heading text-base font-medium tracking-tight">Replies by teammate</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Outgoing reply volume — last 90 days.</p>
        </div>
        <div className="p-4 space-y-2">
          {stats.replies_by_user.length === 0 && (
            <p className="text-sm text-zinc-500" data-testid="replies-empty">No outgoing replies yet.</p>
          )}
          {stats.replies_by_user.map((r) => {
            const max = Math.max(...stats.replies_by_user.map((x) => x.replies), 1);
            const pct = (r.replies / max) * 100;
            return (
              <div key={r.user_id} className="flex items-center gap-3" data-testid={`replies-row-${r.user_id}`}>
                <div className="w-32 text-sm truncate">{r.name}</div>
                <div className="flex-1 h-6 bg-zinc-100 rounded relative overflow-hidden">
                  <div className="h-full bg-[#0033CC] transition-all" style={{ width: `${pct}%` }} />
                </div>
                <div className="w-12 text-right text-sm font-mono">{r.replies}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


function PhoneCell({ user, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(user.phone || "");
  // Re-sync local input when the user prop changes (e.g. after a save the
  // parent reloads /users and the row's phone string updates).
  useEffect(() => {
    if (!editing) setVal(user.phone || "");
  }, [user.phone, editing]);
  const save = async () => {
    try {
      await api.put(`/users/${user.id}`, { phone: val || null });
      toast.success(val ? "Phone updated — user can now log in via SMS" : "Phone removed");
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update phone");
    }
  };
  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="text-xs font-mono text-zinc-600 hover:text-[#0033CC] hover:underline"
        data-testid={`edit-phone-${user.id}`}
      >
        {user.phone || <span className="italic text-zinc-400">add phone</span>}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <Input
        type="tel"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="+15165551234"
        className="h-7 text-xs w-36 font-mono"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") { setEditing(false); setVal(user.phone || ""); }
        }}
        data-testid={`phone-input-${user.id}`}
      />
      <Button size="sm" className="h-7 text-xs px-2" onClick={save} data-testid={`save-phone-${user.id}`}>
        Save
      </Button>
    </div>
  );
}

function BackfillProgress({ status }) {
  const fetched = status.fetched || 0;
  const seen = status.seen || 0;
  const total = status.total_estimate || 0;
  const isRunning = status.status === "running";
  const isDone = status.status === "done";
  const isError = status.status === "error";
  const pct = total > 0 ? Math.min(100, (seen / total) * 100) : (isRunning ? null : 100);

  // Compute rate (msgs/min) + ETA from the started_at timestamp.
  let rate = null;
  let etaLabel = null;
  if (isRunning && status.started_at) {
    const startedMs = new Date(status.started_at).getTime();
    const elapsedSec = (Date.now() - startedMs) / 1000;
    if (elapsedSec > 5 && fetched > 0) {
      rate = fetched / elapsedSec; // msgs per second
      const remaining = Math.max(0, (total || 0) - seen);
      if (rate > 0 && remaining > 0) {
        const etaSec = remaining / rate;
        if (etaSec < 60) etaLabel = "<1 min";
        else if (etaSec < 3600) etaLabel = `~${Math.round(etaSec / 60)} min`;
        else etaLabel = `~${(etaSec / 3600).toFixed(1)} hr`;
      }
    }
  }

  return (
    <div className="text-xs space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className={isError ? "text-red-700 font-medium" : isDone ? "text-emerald-700 font-medium" : "text-zinc-700 font-medium"}>
          {isError ? "Backfill failed" : isDone ? "Backfill complete" : "Backfill in progress"}
        </span>
        <span className="font-mono text-zinc-600">
          {fetched.toLocaleString()} new · {seen.toLocaleString()} seen
          {total ? ` / ${total.toLocaleString()} total` : ""}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-zinc-200 overflow-hidden">
        <div
          className={`h-full ${isError ? "bg-red-500" : isDone ? "bg-emerald-500" : "bg-[#0033CC]"}`}
          style={{ width: pct == null ? "100%" : `${pct}%`, transition: "width 600ms ease" }}
        />
      </div>
      {isRunning && rate != null && (
        <div className="text-[10px] text-zinc-500 flex items-center gap-2">
          <span>~{Math.round(rate * 60).toLocaleString()} msgs/min</span>
          {etaLabel && <span>· ETA {etaLabel}</span>}
        </div>
      )}
      {status.last_error && (
        <p className="text-[10px] text-red-700 mt-1">{status.last_error}</p>
      )}
    </div>
  );
}


// ---------- Audit Log ----------

const AUDIT_CATEGORIES = [
  { id: null,        label: "All",      cls: "bg-zinc-900 text-white" },
  { id: "auth",      label: "Auth",     cls: "bg-blue-50 text-blue-700 border border-blue-200" },
  { id: "users",     label: "Users",    cls: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
  { id: "inboxes",   label: "Inboxes",  cls: "bg-amber-50 text-amber-700 border border-amber-200" },
];

const ACTION_LABELS = {
  login_password: "Signed in with password",
  login_sms_by_email: "Signed in via SMS (email lookup)",
  login_sms_by_phone: "Signed in via SMS",
  login_email_setup: "Signed in & set phone via email code",
  login_failed: "Failed login",
  login_blocked_pending: "Blocked login — account pending",
  login_blocked_disabled: "Blocked login — account disabled",
  sms_verify_failed: "Failed SMS code",
  password_reset_complete: "Reset password",
  abuse_detected_request_flood: "⚠️ Abuse signal — IP flooding code requests",
  create_user: "Created user",
  delete_user: "Deleted user",
  update_user: "Updated user",
  bulk_create_users: "Bulk-imported users",
  approve_signup: "Approved signup",
  decline_signup: "Declined signup",
  self_update_phone: "Updated own phone",
  connect_inbox: "Connected Gmail inbox",
  disconnect_inbox: "Disconnected Gmail inbox",
  start_backfill: "Started Gmail backfill",
};

function formatAuditTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const ms = Date.now() - d.getTime();
  const mins = Math.round(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 14) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
}

function summarizeDetails(action, details, targetEmail) {
  if (!details && !targetEmail) return null;
  const bits = [];
  if (targetEmail) bits.push(targetEmail);
  if (details?.role) {
    const r = details.role;
    if (typeof r === "string") bits.push(`role=${r}`);
    else if (r.from && r.to) bits.push(`role: ${r.from} → ${r.to}`);
  }
  if (details?.active !== undefined) bits.push(details.active ? "enabled" : "disabled");
  if (details?.phone) {
    const p = details.phone;
    if (p.from || p.to) bits.push(`phone ···${p.from || "—"} → ···${p.to || "—"}`);
  }
  if (details?.from_last4 !== undefined || details?.to_last4 !== undefined) {
    bits.push(`phone ···${details.from_last4 || "—"} → ···${details.to_last4 || "—"}`);
  }
  if (details?.created !== undefined) bits.push(`${details.created} created, ${details.skipped} skipped`);
  if (details?.password === "reset") bits.push("password reset");
  if (details?.force) bits.push("forced");
  if (details?.phone_last4) bits.push(`phone ···${details.phone_last4}`);
  if (details?.reason) bits.push(details.reason);
  if (details?.name && !bits.includes(details.name)) bits.push(details.name);
  if (details?.path) bits.push(`(${details.path})`);
  return bits.length ? bits.join(" · ") : null;
}

function AuditLogTab() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_category: {} });
  const [cursor, setCursor] = useState(null);
  const [category, setCategory] = useState(null);
  const [actorFilter, setActorFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchSummary = async () => {
    try { const r = await api.get("/audit-logs/summary"); setSummary(r.data); } catch { /* noop */ }
  };

  const fetchPage = async (opts = {}) => {
    const { append = false, before = null } = opts;
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const params = { limit: 50 };
      if (before) params.before = before;
      if (category) params.category = category;
      if (actorFilter.trim()) params.actor_email = actorFilter.trim();
      const r = await api.get("/audit-logs", { params });
      const newItems = r.data?.items || [];
      setItems((prev) => append ? [...prev, ...newItems] : newItems);
      setCursor(r.data?.next_cursor || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load audit log");
    } finally {
      setLoading(false); setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchPage();
    // Refresh every 30s so newly-logged events appear without manual reload
    const t = setInterval(() => { fetchPage(); fetchSummary(); }, 30000);
    return () => clearInterval(t);
  }, [category]);

  const onSearch = (e) => {
    e?.preventDefault?.();
    fetchPage();
  };

  return (
    <div className="space-y-4" data-testid="audit-log-tab">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="font-heading text-base font-medium">Audit log</h2>
          <p className="text-xs text-zinc-500 mt-0.5">{summary.total.toLocaleString()} total events · refreshes every 30s</p>
        </div>
        <Button
          variant="outline" size="sm" className="h-8 text-xs"
          onClick={() => { fetchPage(); fetchSummary(); }}
          data-testid="audit-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Refresh
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {AUDIT_CATEGORIES.map((c) => {
          const active = category === c.id;
          const count = c.id ? (summary.by_category?.[c.id] || 0) : summary.total;
          return (
            <button
              key={c.id || "all"}
              onClick={() => setCategory(c.id)}
              className={
                "px-2.5 py-1 rounded-full text-xs font-medium transition-all " +
                (active ? c.cls : "bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-400")
              }
              data-testid={`audit-cat-${c.id || "all"}`}
            >
              {c.label} <span className="opacity-60 ml-1">({count.toLocaleString()})</span>
            </button>
          );
        })}
        <form onSubmit={onSearch} className="ml-auto flex items-center gap-1.5">
          <Filter className="w-3.5 h-3.5 text-zinc-400" strokeWidth={1.5} />
          <Input
            type="email" placeholder="filter by actor email…"
            value={actorFilter} onChange={(e) => setActorFilter(e.target.value)}
            className="h-8 text-xs w-56" data-testid="audit-actor-filter"
          />
          <Button type="submit" variant="outline" size="sm" className="h-8 text-xs" data-testid="audit-actor-search">
            Filter
          </Button>
        </form>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 py-10 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-zinc-200 rounded-lg py-12 text-center text-sm text-zinc-500" data-testid="audit-empty">
          No audit events match these filters yet.
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
          <ul className="divide-y divide-zinc-100">
            {items.map((row) => {
              const cat = AUDIT_CATEGORIES.find((c) => c.id === row.category) || AUDIT_CATEGORIES[0];
              const action = ACTION_LABELS[row.action] || row.action;
              const summary = summarizeDetails(row.action, row.details, row.target_email);
              return (
                <li
                  key={row.id}
                  className={"px-3 py-2.5 flex items-start gap-3 hover:bg-zinc-50 transition-colors " + (row.success === false ? "bg-red-50/30" : "")}
                  data-testid={`audit-row-${row.id}`}
                >
                  <span className={"shrink-0 mt-0.5 px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider " + cat.cls}>
                    {row.category || "event"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-medium text-zinc-900">{row.actor_name || row.actor_email || "Anonymous"}</span>
                      <span className="text-zinc-500"> — {action}</span>
                      {row.success === false && (
                        <span className="ml-1.5 text-[10px] text-red-600 font-medium uppercase">failed</span>
                      )}
                    </p>
                    {summary && (
                      <p className="text-xs text-zinc-500 truncate mt-0.5">{summary}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0 text-[11px] text-zinc-400 font-mono leading-tight">
                    <div title={row.created_at}>{formatAuditTime(row.created_at)}</div>
                    {row.ip && <div className="opacity-60">{row.ip}</div>}
                  </div>
                </li>
              );
            })}
          </ul>
          {cursor && (
            <div className="border-t border-zinc-100 p-2.5 text-center">
              <Button
                variant="ghost" size="sm" className="h-8 text-xs"
                onClick={() => fetchPage({ append: true, before: cursor })}
                disabled={loadingMore}
                data-testid="audit-load-more"
              >
                <Clock className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                {loadingMore ? "Loading…" : "Load older events"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

